// components/FollowUpGap.js
import React, { useState, useEffect, useCallback } from "react";
import { getFollowUpGap, getFollowUpGapLeads } from "../api";

const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "#6B7280",
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: ".4px",
};

const inputStyle = {
  height: 38,
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  padding: "0 10px",
  background: "#fff",
};

const pill = (bg, color) => ({
  display: "inline-block",
  minWidth: 36,
  padding: "3px 8px",
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 700,
  background: bg,
  color,
});

// Din ke hisaab se colour: 15-29 yellow, 30-59 orange, 60+ red
const gapColor = (d) =>
  d >= 60
    ? { bg: "#FEF2F2", color: "#DC2626" }
    : d >= 30
    ? { bg: "#FFF7ED", color: "#C2410C" }
    : { bg: "#FFFBEB", color: "#B45309" };

const fmtDate = (v) =>
  v ? new Date(v).toLocaleDateString("en-IN") : "—";

export default function FollowUpGap({ salesManagers = [], isSalesManager }) {
  const [days, setDays] = useState(15);
  const [assignedTo, setAssignedTo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openId, setOpenId] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setOpenId(null);

      const filters = { days };
      if (assignedTo) filters.assigned_to = assignedTo;

      const res = await getFollowUpGap(filters);
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("FOLLOW-UP GAP LOAD ERROR:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [days, assignedTo]);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const toggleRow = async (row) => {
    if (openId === row.sales_manager_id) {
      setOpenId(null);
      return;
    }

    try {
      setOpenId(row.sales_manager_id);
      setLeadsLoading(true);
      setLeads([]);

      const res = await getFollowUpGapLeads({
        user_id: row.sales_manager_id,
        days,
      });

      setLeads(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("FOLLOW-UP GAP LEADS ERROR:", err);
      alert(err.response?.data?.error || "Could not load leads");
    } finally {
      setLeadsLoading(false);
    }
  };

  const sum = (key) => rows.reduce((s, r) => s + Number(r[key] || 0), 0);

  const totals = {
    active: sum("active_leads"),
    stale: sum("stale_leads"),
    b1: sum("band_1"),
    b2: sum("band_2"),
    b3: sum("band_3"),
    never: sum("never_contacted"),
    max: rows.reduce((m, r) => Math.max(m, Number(r.max_gap_days || 0)), 0),
  };

  const totalPercent =
    totals.active > 0
      ? ((totals.stale / totals.active) * 100).toFixed(1)
      : "0.0";

  const worst = rows.length
    ? [...rows].sort((a, b) => b.stale_leads - a.stale_leads)[0]
    : null;

  const exportCsv = () => {
    const header = [
      "Sales Manager / TL", "Active Leads", `Stale (${days}+)`, "Stale %",
      `${days}-29`, "30-59", "60+", "Never Contacted", "Max Gap (days)",
    ];
    const lines = rows.map((r) => [
      r.sales_manager, r.active_leads, r.stale_leads, r.stale_percent,
      r.band_1, r.band_2, r.band_3, r.never_contacted, r.max_gap_days,
    ]);
    const csv = [header, ...lines]
      .map((l) => l.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `follow-up-gap-${days}d.csv`;
    a.click();
  };

    return (
    <div className="fug">
      <style>{`
        .fug th, .fug td { text-align: center !important; }
        .fug th:first-child:not([colspan]),
        .fug td:first-child:not([colspan]) { text-align: left !important; }
        .fug .fug-leads th:nth-child(3),
        .fug .fug-leads td:nth-child(3) { text-align: left !important; }
      `}</style>

      {/* FILTER BAR */}
      <div
        className="card"
        style={{
          marginBottom: 16,
          padding: 16,
          display: "flex",
          alignItems: "flex-end",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <label style={labelStyle}>No follow-up for</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ ...inputStyle, minWidth: 140 }}
          >
            <option value={7}>7+ days</option>
            <option value={15}>15+ days</option>
            <option value={30}>30+ days</option>
            <option value={45}>45+ days</option>
            <option value={60}>60+ days</option>
          </select>
        </div>

        {!isSalesManager && (
          <div>
            <label style={labelStyle}>Sales Manager</label>
            <select
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              style={{ ...inputStyle, minWidth: 200 }}
            >
              <option value="">All Sales Managers / Team Leads</option>
              {salesManagers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            height: 38,
            padding: "0 22px",
            border: "none",
            borderRadius: 8,
            background: "#1D9E75",
            color: "#fff",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Loading..." : "Apply Filters"}
        </button>

        <button
          type="button"
          onClick={exportCsv}
          disabled={!rows.length}
          style={{
            height: 38,
            padding: "0 18px",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            background: "#fff",
            color: "#374151",
            fontWeight: 600,
            cursor: rows.length ? "pointer" : "not-allowed",
          }}
        >
          Export CSV
        </button>
      </div>

      {/* KPI CARDS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div className="metric-card">
          <div className="metric-label">Stale Leads</div>
          <div
            className="metric-value"
            style={{ color: totals.stale > 0 ? "#DC2626" : "#111827" }}
          >
            {totals.stale}
          </div>
          <div className="metric-sub">{days}+ days no follow-up</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">% of Active Leads</div>
          <div className="metric-value">{totalPercent}%</div>
          <div className="metric-sub">of {totals.active} active</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">Never Contacted</div>
          <div className="metric-value" style={{ color: "#DC2626" }}>
            {totals.never}
          </div>
          <div className="metric-sub">Zero follow-ups</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">{days}–29 days</div>
          <div className="metric-value" style={{ color: "#B45309" }}>
            {totals.b1}
          </div>
          <div className="metric-sub">Warning</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">30–59 days</div>
          <div className="metric-value" style={{ color: "#C2410C" }}>
            {totals.b2}
          </div>
          <div className="metric-sub">Serious</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">60+ days</div>
          <div className="metric-value" style={{ color: "#DC2626" }}>
            {totals.b3}
          </div>
          <div className="metric-sub">Critical</div>
        </div>

        <div className="metric-card">
          <div className="metric-label">Most Pending</div>
          <div className="metric-value" style={{ fontSize: 16 }}>
            {worst && worst.stale_leads > 0 ? worst.sales_manager : "—"}
          </div>
          <div className="metric-sub">
            {worst && worst.stale_leads > 0
              ? `${worst.stale_leads} stale leads`
              : "No stale leads"}
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid #F0F0F0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
              Follow-up Gap
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
              Active leads with no follow-up in {days}+ days — click a row to see leads
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#6B7280" }}>
            {rows.length} Sales Managers / Team Leads
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#6B7280" }}>
              Loading follow-up gap report...
            </div>
          ) : (
            <table style={{ width: "100%", minWidth: 1100, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F9FAFB" }}>
                  <th style={{ textAlign: "left", minWidth: 200 }}>
                    Sales Manager / Team Lead
                  </th>
                  <th>Active Leads</th>
                  <th>Stale ({days}+)</th>
                  <th>Stale %</th>
                  <th>{days}–29</th>
                  <th>30–59</th>
                  <th>60+</th>
                  <th>Never Contacted</th>
                  <th>Max Gap (days)</th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}
                    >
                      No data found
                    </td>
                  </tr>
                ) : (
                  <>
                    {/* TOTAL */}
                    <tr
                      style={{
                        background: "#F3F4F6",
                        borderBottom: "2px solid #D1D5DB",
                        fontWeight: 700,
                      }}
                    >
                      <td style={{ fontWeight: 800 }}>Total</td>
                      <td>{totals.active}</td>
                      <td style={{ color: "#DC2626" }}>{totals.stale}</td>
                      <td>{totalPercent}%</td>
                      <td>{totals.b1}</td>
                      <td>{totals.b2}</td>
                      <td>{totals.b3}</td>
                      <td>{totals.never}</td>
                      <td>{totals.max}</td>
                    </tr>

                    {rows.map((row) => {
                      const isOpen = openId === row.sales_manager_id;
                      const pc = Number(row.stale_percent || 0);

                      return (
                        <React.Fragment key={row.sales_manager_id}>
                          <tr
                            onClick={() => toggleRow(row)}
                            style={{
                              borderBottom: "1px solid #F3F4F6",
                              cursor: "pointer",
                              background: isOpen ? "#F0FDF4" : undefined,
                            }}
                          >
                            <td style={{ fontWeight: 700, color: "#111827", whiteSpace: "nowrap" }}>
                              <span style={{ marginRight: 6, color: "#1D9E75" }}>
                                {isOpen ? "▼" : "▶"}
                              </span>
                              {row.sales_manager}
                            </td>
                            <td>{row.active_leads}</td>
                            <td style={{ fontWeight: 700, color: row.stale_leads > 0 ? "#DC2626" : "#15803D" }}>
                              {row.stale_leads}
                            </td>
                            <td>
                              <span
                                style={
                                  pc >= 50
                                    ? pill("#FEF2F2", "#DC2626")
                                    : pc >= 25
                                    ? pill("#FFFBEB", "#B45309")
                                    : pill("#ECFDF5", "#15803D")
                                }
                              >
                                {pc}%
                              </span>
                            </td>
                            <td>{row.band_1}</td>
                            <td>{row.band_2}</td>
                            <td style={{ color: row.band_3 > 0 ? "#DC2626" : undefined, fontWeight: row.band_3 > 0 ? 700 : 400 }}>
                              {row.band_3}
                            </td>
                            <td style={{ color: row.never_contacted > 0 ? "#DC2626" : undefined, fontWeight: row.never_contacted > 0 ? 700 : 400 }}>
                              {row.never_contacted}
                            </td>
                            <td>{row.max_gap_days}</td>
                          </tr>

                          {isOpen && (
                            <tr>
                              <td colSpan="9" style={{ padding: 0, background: "#FAFAFA" }}>
                                {leadsLoading ? (
                                  <div style={{ padding: 24, textAlign: "center", color: "#6B7280" }}>
                                    Loading leads...
                                  </div>
                                ) : leads.length === 0 ? (
                                  <div style={{ padding: 24, textAlign: "center", color: "#9CA3AF" }}>
                                    No stale leads
                                  </div>
                                ) : (
                                  <div style={{ maxHeight: 420, overflow: "auto" }}>
                                    <table className="fug-leads" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                                      <thead>
                                        <tr style={{ background: "#F3F4F6" }}>
                                          <th style={{ textAlign: "left" }}>Lead</th>
                                          <th style={{ textAlign: "left" }}>Phone</th>
                                          <th style={{ textAlign: "left" }}>Stage</th>
                                          
                                          <th>Last Follow-up</th>
                                          <th>Days</th>
                                          <th>Next Task</th>
                                          <th>Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {leads.map((l) => {
                                          const c = gapColor(Number(l.days_gap));
                                          return (
                                            <tr key={l.id} style={{ borderTop: "1px solid #EEE" }}>
                                              <td style={{ fontWeight: 600 }}>{l.name || "-"}</td>
                                              <td>{l.phone || "-"}</td>
                                              <td>{l.stage || "-"}</td>
                                              <td style={{ textAlign: "center" }}>
                                                {l.never_contacted ? (
                                                  <span style={pill("#FEF2F2", "#DC2626")}>Never</span>
                                                ) : (
                                                  fmtDate(l.last_followup_at)
                                                )}
                                              </td>
                                              <td style={{ textAlign: "center" }}>
                                                <span style={pill(c.bg, c.color)}>{l.days_gap}</span>
                                              </td>
                                              <td style={{ textAlign: "center" }}>
                                                {l.next_task_date ? fmtDate(l.next_task_date) : "None"}
                                              </td>
                                              <td style={{ textAlign: "center" }}>
                                                <a
                                                  href={`/leads/${l.id}`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{ color: "#1D9E75", fontWeight: 700 }}
                                                >
                                                  Open Lead
                                                </a>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}